import type { FC } from 'react'
import type { CardMeta } from '../dashboard/types'
import type { QHSEDefectDto } from '../../../QHSE/core/_models'
import CardInfoButton from './CardInfoButton'
import { useNavigate } from 'react-router-dom'
import { isOpenDashboardDefect, dashboardReportName } from './dashboardData'

type WorkQueueProps = {
  createdReports: any[]
  defects: QHSEDefectDto[]
  onViewAll: () => void
}

const meta: CardMeta = {
  description: 'Actionable work items requiring attention — draft reports needing submission and open defects requiring review.',
  endpoint: 'GET /reports (filter: status=DRAFT) | GET /api/qhse/defects (filter: activeOnly=true)',
  logic: 'Draft position reports and all active unclosed defects. Age uses creation/observation dates; no deadline or priority is inferred.',
}

const WorkItem: FC<{ icon: string; color: string; title: string; location: string; due: string; action: string }> = ({ icon, color, title, location, due, action }) => {
  const navigate = useNavigate()
  const iconMap: Record<string, string> = {
    fuel: '/media/icons/duotune/general/gen034.svg',
    'file-text': '/media/icons/duotune/files/fil008.svg',
    route: '/media/icons/duotune/maps/map002.svg',
    calendar: '/media/icons/duotune/general/gen014.svg',
    container: '/media/icons/duotune/finance/fin001.svg',
  }
  return (
    <div className='d-flex align-items-start gap-2 p-2 rounded border' style={{ borderColor: '#eee' }}>
      <div className={`rounded d-flex align-items-center justify-content-center flex-shrink-0 bg-light-${color}`} style={{ width: '26px', height: '26px' }}>
        <img src={iconMap[icon] || iconMap.fuel} alt='' className={`svg-icon-2 text-${color}`} />
      </div>
      <div className='flex-1 min-w-0'>
        <div className='text-dark' style={{ fontSize: '10px' }}>{title}</div>
        <div className='text-muted' style={{ fontSize: '9px' }}>{location}</div>
      </div>
      <div className='text-end flex-shrink-0'>
        <div className='text-muted' style={{ fontSize: '9px' }}>Age: {due}</div>
        <div><button className='btn btn-sm btn-primary mt-1' onClick={() => navigate(action === 'Submit' ? '/operations/overview' : '/operations/defects-list')} style={{ fontSize: '9px', borderRadius: '6px', padding: '1px 8px' }}>Open page</button></div>
      </div>
    </div>
  )
}

const WorkQueue: FC<WorkQueueProps> = ({ createdReports, defects, onViewAll }) => {
  const draftReports = createdReports.filter((r: any) => r.status === 'DRAFT').slice(0, 3)
  const openDefects = defects.filter(isOpenDashboardDefect).slice(0, 2)

  return (
    <div className='flex flex-col flex-1'>
      <div className='card flex-1' style={{ borderRadius: '16px' }}>
        <div className='card-body'>
          <div className='d-flex align-items-center gap-2 mb-3'>
            <h6 className='fw-bold text-dark mb-0' style={{ fontSize: '13px' }}>Actionable Work Queue</h6>
            <span className='ms-auto d-flex align-items-center gap-2'>
              <CardInfoButton meta={meta} />
              <a href='#' className='fw-bold' style={{ fontSize: '10px' }} onClick={(e) => { e.preventDefault(); onViewAll(); }}>View all</a>
            </span>
          </div>
          <div className='d-flex flex-column gap-2'>
            {draftReports.length === 0 && openDefects.length === 0 ? (
              <div className='text-center text-muted py-4' style={{ fontSize: '11px' }}>
                <div className='mb-1'>No pending work items</div>
                <div style={{ fontSize: '10px' }}>All reports submitted and no open defects</div>
              </div>
            ) : (
              <>
                {draftReports.map((r: any, i: number) => (
                  <WorkItem key={`r-${i}`} icon='file-text' color='danger'
                    title={`Submit report: ${dashboardReportName(r)}`}
                    location={r.vesselName || 'Vessel name unavailable'}
                    due={r.createdDateTime ? `${Math.round((Date.now() - new Date(r.createdDateTime).getTime()) / (1000 * 60 * 60))}h` : '—'}
                    action='Submit' />
                ))}
                {openDefects.map((d, i) => (
                  <WorkItem key={`d-${i}`} icon='fuel' color='warning'
                    title={`Defect #${d.defectNumber || d.id}: ${(d.description || 'Review').slice(0, 30)}`}
                    location={d.vesselName || 'Unknown'}
                    due={d.dateObserved ? `${Math.round((Date.now() - new Date(d.dateObserved).getTime()) / (1000 * 60 * 60 * 24))}d` : '—'}
                    action='Review' />
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default WorkQueue
